import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { IUiError } from '../../../shared/model/IError.model';
import { SharedService } from '../../../shared/shared.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IEnrolledState, IMFAData, ITenantInfo } from '../../../shared/model/IMfa.model';

@Component({
  selector: 'verification-enroll-face',
  templateUrl: './face.component.html',
  styleUrls: ['./face.component.scss'],
})
export class FaceComponent implements OnInit {
  @Input() config!: ITenantInfo;
  @Input() track_id!: string;
  @Output() checkEnrollmentStatus = new EventEmitter();

  // will control the screen flow [screen1, screen2, screen3]
  page$ = new BehaviorSubject('');

  // will handle the actions from the authenticator app
  appAction$ = new BehaviorSubject('');

  // will handle the destroy event for the component
  destroy$ = new Subject();

  // will hold the qr string
  qrData: string = '';

  // will hold the authenticator app string
  authAppURL: string = '';

  // will hold the enrollment success respose
  successState$ = new BehaviorSubject(null);

  //will hold the error
  errorState$ = new BehaviorSubject(<IUiError>{});

  // use to unsubscribe the socket listening
  stopSocketListening$ = new Subject();

  AuthenticatorBtnOptions = {
    color: 'primary',
    text: 'SUGGESTED_VERIFICATION_METHOD.ENROLLMENT.OPEN_AUTHENTICATOR',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasSubmitBtn',
    translateArgs: {},
  };

  cancelBtnOptions = {
    color: 'accent',
    text: 'SUGGESTED_VERIFICATION_METHOD.CANCEL',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasCancelBtn',
    translateArgs: {},
    style: 'secondary',
  };

  continueBtnOptions = {
    color: 'accent',
    text: 'SUGGESTED_VERIFICATION_METHOD.CONTINUE',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasSubmitBtn',
    translateArgs: {},
  };

  constructor(private _sharedService: SharedService) {}

  ngOnInit(): void {
    if ((this.track_id, Object.keys(this.config || {}).length)) {
      this.successState$.next(null);
      this.errorState$.next(<IUiError>{});
      this._initiateEnrollmentProcess(this.track_id as string)
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged()
        )
        .subscribe(
          (mfaStatus) => {
            this.qrData = this._constructQR(mfaStatus, this.config as ITenantInfo);
            this.authAppURL = this._constructAuthAppURL(mfaStatus, this.config as ITenantInfo);
            this.page$.next('screen1');
            this._listenAuthenticatorStatus(mfaStatus?.status_id);
          },
          (err) => {
            // preparing the error data for the UI
            const default_message = 'ERROR_CODES.SUGGEST_VERIFICATION.ENROLL_INITIATE.DEFAULT';
            const message = err.statusCode && err.code
                  &&`ERROR_CODES.SUGGEST_VERIFICATION.ENROLL_INITIATE.STATUS_${err.code}`
                  || default_message;
            this.errorState$.next({message, default_message, status: err?.statusCode});
          }
        );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  _cancelEnrollmentProcess = () => {
    this.checkEnrollmentStatus.emit(false);
  };

  /**
   *
   * @returns boolean
   * it will return whether its a mobile device or not
   */
  _isMobile = (): Observable<boolean | null> => {
    return this._sharedService._isMobile().pipe(distinctUntilChanged());
  };

  /**
   *
   * @param trackId which is taken from the query params
   * @returns the MFA initiate respose as observable
   */
  _initiateEnrollmentProcess = (trackId: string): any => {
    return this._sharedService._initiateSuggestVerification(trackId, 'face');
  };

  /**
   *
   * @param data will hold the MFA initiate response
   * @param tenantInfo will hold the tenant related information
   * @returns the QR string to open the cidaas authenticator app
   *
   * Will construct the QR code string to render the QR code
   */
  _constructQR = (data: IMFAData, tenantInfo: ITenantInfo): string => {
    return this._sharedService._constructQRData(data, tenantInfo, 'FACE');
  };

  /**
   *
   * @param data will hold the MFA initiate response
   * @param tenantInfo will hold the tenant related information
   * @returns the Query string to open the cidaas authenticator app
   *
   * Will construct url for the authenticator app to open in mobile devices
   */
  _constructAuthAppURL = (data: IMFAData, tenantInfo: ITenantInfo): string => {
    return this._sharedService._constructAppQuery(data, tenantInfo, 'FACE');
  };

  /**
   * Will open authenticator app in mobile
   */
  _openAuthenticatorApp = (): void => {
    this._sharedService._openAuthenticatorApp(this.authAppURL);
  };

  /**
   *
   * @param e will hold the enrolled state
   * Will emit enrolled state to the parent component
   */
  _completeEnrollmentProcess = (e:IEnrolledState) => {
    this.checkEnrollmentStatus.emit(e);
  };

  /**
   *
   * @param status_id
   *
   * will listen for the app actions from the authenticator app
   * based on the the actions will controll the flow
   *
   * the subscription will end if the component is destroyed or we manually trigger it
   * here we are using stopSocketListening$ to tigger the unsubscription
   */
  _listenAuthenticatorStatus = (status_id: string) => {
    this._sharedService
      ._listenSocketStatus(status_id)
      .pipe(distinctUntilChanged(), takeUntil(merge(this.destroy$, this.stopSocketListening$)))
      .subscribe((res) => {
        const action = res?.data?.status;
        this.appAction$.next(action);
        if (action === 'SCANNED') this.page$.next('screen2');
        if (action === 'CANCELLED') {
          this.stopSocketListening$.next();
          this._cancelEnrollmentProcess();
        }
        if (action === 'ENROLLED') {
          this.stopSocketListening$.next();
          this.successState$.next(res?.data);
        }
      });
  };
}
